import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, PauseCircle, PlayCircle, Robot, XCircle } from "@phosphor-icons/react";
import { Switch } from "@/components/ui/switch";
import { adminApi, getErrorMessage } from "@/lib/api";
import { Card, btnGhost } from "../ui";
import { fmtDate } from "./parts";
import { AutoApproveStep } from "./AutoApproveStep";

function Step({ n, title, children, ok, testId }) {
  return (
    <div className={`rounded-xl border p-4 ${ok ? "border-[#73bf44]/30 bg-[#73bf44]/[0.04]" : "border-neutral-800 bg-black/30"}`} data-testid={testId}>
      <div className="flex items-center gap-2 text-[0.62rem] uppercase tracking-[0.16em] font-bold text-neutral-500 mb-2">
        <span className="tabular-nums">{n}</span> {title}
      </div>
      {children}
    </div>
  );
}

function LiveStatus({ job }) {
  if (!job) return <p className="text-[0.7rem] text-neutral-600 mt-2">No automatic check has run yet.</p>;
  const running = job.status === "running";
  return (
    <p className="text-[0.7rem] text-neutral-500 mt-2" data-testid="wi-live-status">
      {running ? `Checking claims… ${job.done || 0} / ${job.todo || 0}` : `Last check ${fmtDate(job.finished_at || job.heartbeat)} · ${job.assessed ?? 0} assessed`}
      {job.status === "failed" && <span className="text-red-400"> · failed: {job.error}</span>}
    </p>
  );
}

export function ActivationPanel({ ov, onChange }) {
  const [busy, setBusy] = useState(false);
  const put = async (patch, msg) => {
    setBusy(true);
    try { onChange((await adminApi.put("/admin/wi/assess/activation", patch)).data); toast.success(msg); }
    catch (e) { toast.error(getErrorMessage(e)); } finally { setBusy(false); }
  };
  const on = ov.ai_assessment_enabled;
  return (
    <Card title="Activation" icon={Robot} testId="wi-activation-panel" actions={
      <button className={`${btnGhost} ${ov.emergency_pause ? "!border-red-500/60 !text-red-300" : ""}`} disabled={busy} data-testid="wi-emergency-pause-btn"
        onClick={() => put({ emergency_pause: !ov.emergency_pause }, ov.emergency_pause ? "Emergency pause lifted" : "All AI assessment paused")}>
        {ov.emergency_pause ? <PlayCircle size={15} /> : <PauseCircle size={15} />} {ov.emergency_pause ? "Lift emergency pause" : "Emergency pause"}
      </button>
    }>
      {ov.emergency_pause && <div className="mb-4 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="wi-pause-banner">Emergency pause is on — no claims are being assessed.</div>}
      <div className="grid md:grid-cols-3 gap-3">
        <Step n="1" title="Historical database" ok={ov.validated} testId="wi-step-validated">
          {ov.validated ? (
            <p className="text-sm text-[#73bf44] flex items-center gap-1.5"><CheckCircle size={16} weight="fill" /> Validated {fmtDate(ov.validated_at)}</p>
          ) : (
            <p className="text-sm text-neutral-300 flex items-start gap-1.5"><XCircle size={16} className="text-amber-400 shrink-0 mt-0.5" />
              <span>Not validated yet. <Link to="/admin/warranty-intel" className="text-[#73bf44] hover:underline">Review & sign off</Link> first{ov.import_status === "running" ? " (import still running)" : ""}.</span></p>
          )}
        </Step>
        <Step n="2" title="AI checks on new claims" ok={on} testId="wi-step-ai">
          <label className="flex items-center justify-between gap-3 text-sm text-neutral-200">
            <span>{on ? "On · suggestions only" : "Off"}</span>
            <Switch checked={on} disabled={busy || !ov.validated} onCheckedChange={(v) => put({ ai_assessment_enabled: v }, v ? "AI assessment switched on (suggestions only)" : "AI assessment switched off")} data-testid="wi-ai-toggle" />
          </label>
          <p className="text-[0.7rem] text-neutral-500 mt-1.5">Scores every new or open claim and suggests an outcome. Nothing is approved, emailed or written to Monday.com.</p>
          {on && <LiveStatus job={ov.live_job} />}
        </Step>
        <Step n="3" title="Automatic approvals by the AI" ok={ov.auto_approvals_enabled} testId="wi-step-auto">
          <AutoApproveStep ov={ov} onChange={onChange} busy={busy} />
        </Step>
      </div>
    </Card>
  );
}
